// U4 placeholder gate: drafts mark facts the model can't know as `[[fill: the date]]`. A draft with
// any placeholder left can't be armed or sent; the owner fills each one in first. Pure and
// zod-free, so the web bundle, the core and the gateway share one reading of the syntax.

/** One `[[fill: …]]` in a draft, with where it sits in the text. */
export interface Placeholder {
  /** What to fill in ("the date"), as the draft names it. */
  label: string;
  /** The placeholder as written, brackets included. */
  raw: string;
  start: number;
  end: number;
}

const PLACEHOLDER = /\[\[\s*fill\s*:\s*([^\]\n]*?)\s*\]\]/gi;
/** Any remnant of the syntax (an unclosed `[[fill:` too): the server refuses to send it. */
const REMNANT = /\[\[\s*fill\s*:/i;

/** Every placeholder in `text`, in order. */
export function findPlaceholders(text: string): Placeholder[] {
  const out: Placeholder[] = [];
  for (const m of text.matchAll(PLACEHOLDER)) {
    const start = m.index ?? 0;
    out.push({
      label: (m[1] ?? "").trim() || "this",
      raw: m[0],
      start,
      end: start + m[0].length,
    });
  }
  return out;
}

/** True while `text` still holds a placeholder (or a broken one) to fill in. */
export function hasPlaceholder(text: string): boolean {
  return REMNANT.test(text);
}

/** The distinct things to fill in, in order ("the date", "the amount"). */
export function placeholderLabels(text: string): string[] {
  return [...new Set(findPlaceholders(text).map((p) => p.label))];
}

/** `text` with every placeholder removed (for checks that must not read its label as content). */
export function stripPlaceholders(text: string): string {
  return text.replace(PLACEHOLDER, " ");
}

/** "Fill in the date before sending." / "Fill in the date and the amount before sending." */
export function placeholderMessage(text: string): string {
  const labels = placeholderLabels(text);
  if (!labels.length) return "Fill in the marked parts of the draft before sending.";
  const list =
    labels.length === 1
      ? labels[0]
      : `${labels.slice(0, -1).join(", ")} and ${labels[labels.length - 1]}`;
  return `Fill in ${list} before sending.`;
}
